import { ReactNode } from 'react';
import CloseIcon from '../../assets/icons/close.svg?react';
import { Button } from '../Button';

interface WizardLayoutProps {
  title: string;
  stepLabels: string[];
  currentStep: number;
  children: ReactNode;
  onNext?: () => void;
  onBack?: () => void;
  onComplete?: () => void;
  completeLabel: string;
  onSaveDraft?: () => void;
  nextDisabled?: boolean;
  error?: string | null;
  onClose: () => void;
  nextLabel?: string;
  cancelLabel?: string;
  saveDraftLabel?: string;
}

export function WizardLayout({
  title,
  stepLabels,
  currentStep,
  children,
  onNext,
  onBack,
  onComplete,
  completeLabel,
  onSaveDraft,
  nextDisabled,
  error,
  onClose,
  nextLabel = 'Next',
  cancelLabel = 'Cancel',
  saveDraftLabel = 'Save as draft',
}: WizardLayoutProps) {
  const totalSteps = stepLabels.length;
  const isLastStep = currentStep === totalSteps;

  return (
    // The wizard replaces the page it covers, so it keeps the dialog semantics
    // the Radix dialogs it replaced had: assistive tech announces it as a dialog
    // and treats the content behind it as inert.
    <div
      role='dialog'
      aria-modal='true'
      aria-label={title}
      className='flex min-h-0 w-full flex-1 flex-col'
    >
      <div className='flex shrink-0 items-start justify-between bg-surface-primary px-8 pt-5 pb-3'>
        <div className='flex flex-col gap-1'>
          <h2 className='size-h4 font-bold text-primary'>{title}</h2>
          {totalSteps > 1 && (
            <span className='size-p2 text-primary'>
              Step {currentStep} of {totalSteps} - {stepLabels[currentStep - 1] || ''}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary hover:bg-neutral-100'
          aria-label='Close wizard'
        >
          <CloseIcon className='h-6 w-6' />
        </button>
      </div>

      <div className='bg-surface-focus-support border-border-secondary flex min-h-0 flex-1 items-center justify-center overflow-y-auto rounded-lg border'>
        {children}
      </div>

      <div
        className='border-border-secondary bg-surface-primary flex shrink-0
          items-center justify-between border-t px-20 py-4'
      >
        <Button variant='tertiary' onClick={onClose}>
          {cancelLabel}
        </Button>
        <div className='flex flex-col items-end gap-1'>
          {error && <p className='text-sm text-red-600'>{error}</p>}
          <div className='flex items-center gap-3'>
            {currentStep > 1 && (
              <Button variant='secondary' onClick={onBack}>
                Back
              </Button>
            )}
            {isLastStep ? (
              <>
                {onSaveDraft && (
                  <Button variant='secondary' onClick={onSaveDraft}>
                    {saveDraftLabel}
                  </Button>
                )}
                <Button
                  variant='primary'
                  onClick={onComplete}
                  state={nextDisabled ? 'disabled' : 'default'}
                >
                  {completeLabel}
                </Button>
              </>
            ) : (
              <Button
                variant='primary'
                onClick={onNext}
                state={nextDisabled ? 'disabled' : 'default'}
              >
                {nextLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
